import client from '@/services/graphql/client';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { Image, ImageProps } from '@chakra-ui/react';
import { assert } from '@common/errors';
import {
    AnyDepositoryAccount,
    ExternalFinancialEntity,
    ExternalFinancialEntityType,
    isExternalFinancialEntity,
    isLinkedAndConnected,
    isLinkedAndDisconnected,
} from '@common/financialEntities';
import {
    feConfigs,
    FieldConfig,
    FinancialEntityConfig,
} from '@common/financialEntities/config';
import {
    AllFinancialEntitiesDocument,
    EstablishAchInfoDocument,
    FinancialEntityDocument,
} from '@common/graphql/generated';
import {
    depositoryAccountsFromFEs,
    isDisconnectedOrError,
    useFinancialEntitiesContext,
} from '@common/react/financialEntities';
import { getAmountRecurrenceStr } from '@common/react/goals';
import {
    defaultDepositoryAccountErrorMessage,
    depositoryAccountErrorMessageFromApolloError,
} from '@common/react/transactions';
import { Recurrence, TransactionSchedulePlan } from '@common/types';
import { isNumber } from '@common/util/types';
import { FC, ReactNode, useState } from 'react';
import { CurrencyInputter, PercentInputter } from '../common/FormattedInput';
import { RadioInputOption } from '../common/RadioInputGroup';
import { LinkText } from '../common/Typography';
import { showExternalAccountConsentModal } from './ExternalAccountConsentModal';

// Use a fixed height during the account linking flow for smoother transitions
export const ACCOUNT_LINKING_MODAL_CONTENT_HEIGHT = 400;

/*
    Helpers requiring Financial Entity typenames only
*/

export const getConfig = (
    feType: ExternalFinancialEntityType
): FinancialEntityConfig => {
    return feConfigs[feType];
};

/*
    Helpers requiring full Financial Entities
*/

export const getLogoImage = (
    fe: ExternalFinancialEntity
): FC<ImageProps> | undefined => {
    const logo = fe.institution?.logo;
    if (!logo) return;
    const LogoImage: FC<ImageProps> = (props: ImageProps) => (
        <Image src={logo} alt="logo" rounded="full" {...props} />
    );
    return LogoImage;
};

export const GENERIC_RETIREMENT_CONTRIBUTION_LIMIT_CONTENT = (
    <>
        Each type of retirement account has its own contribution limits. For
        more details, the{' '}
        <LinkText href="https://www.irs.gov/retirement-plans/plan-participant-employee/retirement-topics-contributions">
            IRS summarizes limits here
        </LinkText>
        .
    </>
);

export const useSelectableDepositoryAccounts = (): {
    loading: boolean;
    error?: ApolloError;
    accounts: AnyDepositoryAccount[];
} => {
    const { data, loading, error } = useQuery(AllFinancialEntitiesDocument);
    if (!data || loading || error) return { loading, error, accounts: [] };
    const accounts = depositoryAccountsFromFEs(data.financialEntities).filter(
        (account) =>
            account.ownersIncludeMyself &&
            // Either..
            // 1. Link is connected; or
            (isLinkedAndConnected(account) ||
                // 2. Link is disconnected and ACH info exists
                (isLinkedAndDisconnected(account) && account.achInfoExists))
    );
    return { loading, accounts };
};

/**
 * Returns a list of linked financial entities in disconnected or
 * error states belonging to the current user.
 */
export const useMyDisconnectedAccounts = () => {
    const { fes } = useFinancialEntitiesContext();
    if (!fes.data) return [];

    return fes.data.financialEntities
        .filter(isExternalFinancialEntity)
        .filter(isDisconnectedOrError)
        .filter((fe) => fe.isMine);
};

/**
 * Helpers for financial entity fields
 */

export type FieldVal =
    | number
    | string
    | boolean
    | Recurrence
    | TransactionSchedulePlan
    | null
    | undefined;

export const formatValue = (val: FieldVal, field: FieldConfig): ReactNode => {
    if (val === '' || val === undefined || val === null) {
        return 'Unknown';
    }
    const { type } = field;
    switch (type) {
        case 'currency': {
            assert(isNumber(val));
            return new CurrencyInputter().toInputString(val);
        }
        case 'percent': {
            assert(isNumber(val));
            return new PercentInputter().toInputString(val);
        }
        case 'select': {
            assert(field.options);
            const option = (field.options as RadioInputOption[]).find(
                (o: RadioInputOption) => o.value === val
            );
            return option ? option.label : 'empty';
        }
        case 'radioCards': {
            assert(field.options);
            const option = (field.options as RadioInputOption[]).find(
                (o: RadioInputOption) => o.value === val
            );
            return option ? option.label : 'empty';
        }
        case 'text':
        case 'float':
        case 'integer':
        case 'checkbox':
            return String(val);
        case 'transactionSchedulePlan':
            return getAmountRecurrenceStr(val as TransactionSchedulePlan);
        default: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const exhaustiveCheck: never = type;
            return '';
        }
    }
};

export const useCheckDepositoryAccount = ({
    /** This is used to clear the selection when an error is encountered */
    setSelectedAccountId,
}: {
    setSelectedAccountId: (accountId: string | undefined) => void;
}) => {
    const [establishAchInfo] = useMutation(EstablishAchInfoDocument);
    const [errorMessageByAccountId, setErrorMessageByAccountId] = useState<
        Record<string, string>
    >({});

    const handleSelectedAccountError = (accountId: string, e: ApolloError) => {
        setErrorMessageByAccountId((obj) => ({
            ...obj,
            [accountId]: depositoryAccountErrorMessageFromApolloError(e),
        }));
    };

    /**
     * If user has not yet submitted a consent, prompts for consent and then
     * creates an Atomic external account (if one does not already exist).
     *
     * If user has already submitted a consent, creates an Atomic external
     * account (if one does not already exist).
     */
    const checkDepositoryAccount = async (financialEntityId: string) => {
        const account = (
            await client.query({
                query: FinancialEntityDocument,
                variables: { id: financialEntityId },
            })
        ).data.financialEntity;

        if (!account) {
            setErrorMessageByAccountId((obj) => ({
                ...obj,
                [financialEntityId]: defaultDepositoryAccountErrorMessage,
            }));
            setSelectedAccountId(undefined);
            throw new Error('checkDepositoryAccount failed: missing account');
        }

        if (!account.externalAccountConsentExistsForUser) {
            // If user has not yet submitted consent, prompt for consent and
            // try to establish ACH info.
            await new Promise((resolve, reject) =>
                showExternalAccountConsentModal({
                    financialEntityId,
                    onComplete: () =>
                        establishAchInfo({
                            variables: { financialEntityId },
                            onCompleted: () => {
                                // no action needed
                            },
                            onError: (e) => {
                                handleSelectedAccountError(
                                    financialEntityId,
                                    e
                                );
                                setSelectedAccountId(undefined);
                                throw e;
                            },
                        }).then(resolve),
                    onCancel: () => {
                        reject('ExternalAccountConsentModal canceled');
                    },
                })
            );
        } else {
            // If user has already submitted consent, try to establish ACH info.
            await establishAchInfo({
                variables: { financialEntityId },
                onCompleted: () => {
                    // no action needed
                },
                onError: (e) => {
                    handleSelectedAccountError(financialEntityId, e);
                    setSelectedAccountId(undefined);
                    throw e;
                },
            });
        }
    };

    return {
        errorMessageByAccountId,
        handleSelectedAccountError,
        checkDepositoryAccount,
    };
};
